// Apolo Self-Improve Hardening — Stage 1 / E4-WS2 · Bad-runs view.
//
// Lists the user's FAILED runs (outcome=ABORT — incl. `autocorrect_cap` — or a
// turn whose oracle verdicts were red), read from the persisted turn_trace D1
// table. Clicking a run opens its FULL persisted trace AND re-runs it through the
// SAME deterministic replay the loop uses (oracle/replay.js), surfacing the
// Stage-1 binary gate right in the UI: "replay reproduces the recorded outcome".
//
// READ-ONLY. States: loading / error / empty all handled.

import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Bug, CheckCircle2, XCircle, RefreshCw, ChevronLeft } from 'lucide-react';
import { fetchBadRuns, fetchTurnTrace } from '../../../hooks/useTurnTrace.js';
import { replayTurnTrace } from '../../../lib/oracle/replay.js';

function fmtTime(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return '—';
  try { return new Date(Number(ms)).toLocaleString(); } catch { return '—'; }
}

function OutcomeBadge({ outcome }) {
  const abort = outcome === 'ABORT';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${
        abort ? 'bg-red-500/12 text-red-400' : 'bg-emerald-500/12 text-emerald-400'
      }`}
    >
      {abort ? <XCircle size={11} aria-hidden="true" /> : <CheckCircle2 size={11} aria-hidden="true" />}
      {outcome || '—'}
    </span>
  );
}

function RunDetail({ trace, onBack }) {
  const replay = replayTurnTrace(trace);
  return (
    <div className="flex flex-col gap-3 p-3 text-[12px]" data-testid="bad-run-detail">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex w-fit items-center gap-1 text-apolo-textSecondary hover:text-apolo-textPrimary"
      >
        <ChevronLeft size={13} aria-hidden="true" /> Voltar
      </button>

      <div className="rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-2.5">
        <div className="mb-1 flex items-center gap-2">
          <OutcomeBadge outcome={trace.outcome} />
          <span className="font-mono text-[11px] text-apolo-textSecondary">{trace.stopReason || '—'}</span>
        </div>
        <div className="text-apolo-textTertiary">
          {trace.runId}<span className="opacity-50"> · turn </span>{trace.turnId} · {fmtTime(trace.createdAt)}
        </div>
      </div>

      {/* The Stage-1 binary gate, shown live: does replay reproduce the recorded outcome? */}
      <div
        className="rounded-md border p-2.5"
        data-testid="replay-result"
        data-matches={replay.matches ? 'true' : 'false'}
        style={{
          borderColor: replay.matches ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)',
          background: replay.matches ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
        }}
      >
        <div className="flex items-center gap-1.5 font-medium">
          {replay.matches ? <CheckCircle2 size={13} className="text-emerald-400" /> : <XCircle size={13} className="text-red-400" />}
          Replay determinístico: {replay.matches ? 'reproduz' : 'DIVERGE'} → {replay.outcome}
        </div>
        <div className="mt-1 text-apolo-textTertiary">
          {replay.deterministic
            ? `Re-derivado dos verdicts gravados (attempts=${replay.attempts}).`
            : 'Estado terminal não-oráculo (honrado como gravado).'}
        </div>
      </div>

      <div>
        <div className="mb-1 font-medium text-apolo-textSecondary">Verdicts ({trace.verdicts?.length || 0})</div>
        {(trace.verdicts || []).length === 0 ? (
          <div className="text-apolo-textTertiary">Nenhum verdict gravado.</div>
        ) : (
          <ul className="flex flex-col gap-1">
            {trace.verdicts.map((v, i) => (
              <li key={`${v.verifier}-${i}`} className="flex items-start gap-1.5 rounded bg-apolo-surface2 px-2 py-1">
                {v.passed ? <CheckCircle2 size={11} className="mt-0.5 text-emerald-400" /> : <XCircle size={11} className="mt-0.5 text-red-400" />}
                <span className="font-mono text-[11px]">{v.verifier}</span>
                <span className="text-apolo-textTertiary">{v.evidence}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {trace.input ? (
        <div>
          <div className="mb-1 font-medium text-apolo-textSecondary">Input</div>
          <pre className="max-h-32 overflow-auto whitespace-pre-wrap rounded bg-apolo-surface2 p-2 text-[11px] text-apolo-textTertiary">{trace.input}</pre>
        </div>
      ) : null}
    </div>
  );
}

export default function BadRunsPanel() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [opening, setOpening] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const list = await fetchBadRuns({ limit: 100 });
      setRuns(Array.isArray(list) ? list : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openRun = useCallback(async (run) => {
    setOpening(true);
    try {
      const full = await fetchTurnTrace(run.runId, run.turnId);
      setSelected(full || run);
    } finally {
      setOpening(false);
    }
  }, []);

  if (selected) return <RunDetail trace={selected} onBack={() => setSelected(null)} />;

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 text-[12px] text-apolo-textSecondary" role="status" aria-live="polite" data-testid="bad-runs">
        <Loader2 size={14} className="animate-spin" aria-hidden="true" /> Carregando runs…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 text-[12px] text-red-400" role="alert" data-testid="bad-runs">
        <AlertTriangle size={14} aria-hidden="true" />
        <span>Não foi possível carregar os bad runs.</span>
        <button type="button" onClick={load} className="text-apolo-accent underline-offset-2 hover:underline">Tentar de novo</button>
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 text-[12px] text-apolo-textSecondary" role="status" data-testid="bad-runs">
        <Bug size={14} aria-hidden="true" /> Nenhum run com falha registrado. 🎉
      </div>
    );
  }

  return (
    <div className="flex flex-col p-2 text-[12px]" data-testid="bad-runs" aria-label="Runs com falha">
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="text-apolo-textTertiary">{runs.length} run(s) com falha</span>
        <button
          type="button"
          onClick={load}
          aria-label="Recarregar"
          className="inline-flex h-6 w-6 items-center justify-center rounded text-apolo-textTertiary hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
        >
          <RefreshCw size={12} aria-hidden="true" />
        </button>
      </div>
      <ul className="flex flex-col gap-1">
        {runs.map((run) => (
          <li key={`${run.runId}-${run.turnId}`}>
            <button
              type="button"
              onClick={() => openRun(run)}
              disabled={opening}
              className="flex w-full items-center gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1.5 text-left transition hover:bg-apolo-surface2 disabled:opacity-60"
            >
              <OutcomeBadge outcome={run.outcome} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-[11px] text-apolo-textPrimary">{run.stopReason || run.runId}</span>
                <span className="block truncate text-[11px] text-apolo-textTertiary">{run.model || '—'} · {fmtTime(run.createdAt)}</span>
              </span>
              {run.capHit ? <span className="shrink-0 rounded bg-amber-500/12 px-1 py-0.5 text-[10px] text-amber-400">cap</span> : null}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
